import api from "@/api/api";
import { getTagList } from "@/api/tag";
import { EVENT_TYPES } from "@/views/MultiDimen/components/constants";

// 格式化接口返回的树形数据
function formatTimbreOptions(data) {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => {
    // 1. 基础映射：label 用 name，value 用 code (若 code 为空则用 id 保证唯一性)
    const result = {
      label: item.name,
      value: item.code || String(item.id), // 确保是字符串，避免类型不一致
      // 保存原始数据中的关键信息，方便后续 extension 拼装
      cardCode: item.timbreCardCode || "",
      code: item.code,
      id: String(item.categoryId),
    };

    // 2. 合并子节点：childList 和 childDataList 都要递归处理
    let children = [];

    // 处理目录级子节点
    if (item.childList && item.childList.length > 0) {
      children = children.concat(formatTimbreOptions(item.childList));
    }

    // 处理数据级子节点（即具体的音色）
    if (item.childDataList && item.childDataList.length > 0) {
      children = children.concat(formatTimbreOptions(item.childDataList));
    }

    // 3. 如果有子节点，则挂载到 children 属性下
    if (children.length > 0) {
      result.children = children;
    }

    return result;
  });
}

export default {
  namespaced: true,
  state: {
    // 拨片数据
    pickList: [],
    // 鼓机分类数据
    drumCategories: [],
    // 轨道音色选项
    trackNoteOptions: [],
    // 数据加载状态
    dataLoaded: {
      pick: false,
      drum: false,
    },

    pageConfig: {
      pageSize: 1000,
      pageNum: 1,
    },

    // 歌曲元数据标签
    songMetadataTags: {},

    // 拨片和鼓机配置
    rhythmConfig: {
      rhythmicPatternA: [],
      pickAVolume: 100,
      pickAExternalVolume: 100,
      rhythmicPatternB: [],
      pickBVolume: 100,
      pickBExternalVolume: 100,
      rhythmicDrumA: [],
      drumAVolume: 100,
      drumAExternalVolume: 100,
      rhythmicDrumB: [],
      drumBVolume: 100,
      drumBExternalVolume: 100,
      // C1原始值
      C1_rhythmicPatternA: "",
      C1_rhythmicPatternB: "",
      C1_rhythmicDrumA: "",
      C1_rhythmicDrumB: "",
    },
    // 外置音色卡配置
    externalTimbreCards: ["sound_c2", "scard_01"],
  },
  mutations: {
    SET_PICK_LIST(state, list) {
      state.pickList = list;
      state.dataLoaded.pick = true;
    },
    SET_DRUM_CATEGORIES(state, categories) {
      state.drumCategories = categories;
      state.dataLoaded.drum = true;
    },
    SET_TRACK_NOTE_OPTIONS(state, trackNoteOptions) {
      state.trackNoteOptions = trackNoteOptions;
    },
    // 设置节奏配置
    SET_RHYTHM_CONFIG(state, config) {
      state.rhythmConfig = { ...state.rhythmConfig, ...config };
    },

    // 重置节奏配置
    RESET_RHYTHM_CONFIG(state) {
      state.rhythmConfig = {
        rhythmicPatternA: [],
        pickAVolume: 100,
        pickAExternalVolume: 100,
        rhythmicPatternB: [],
        pickBVolume: 100,
        pickBExternalVolume: 100,
        rhythmicDrumA: [],
        drumAVolume: 100,
        drumAExternalVolume: 100,
        rhythmicDrumB: [],
        drumBVolume: 100,
        drumBExternalVolume: 100,
        C1_rhythmicPatternA: "",
        C1_rhythmicPatternB: "",
        C1_rhythmicDrumA: "",
        C1_rhythmicDrumB: "",
      };
    },
  },
  actions: {
    // 获取
    async getSongMetadataTags({ state, commit }) {
      try {
        const res = await getTagList();
        if (res?.data?.code === 1) {
          const tagData = res.data.data;
          let result = {};
          if (tagData) {
            // state.songMetadataTags = tagData;
            for (const key in tagData) {
              let element = tagData[key];
              const idEmpty = element.children.some(
                (item) => item.label === "无" || item.label === "无标签"
              );
              if (!idEmpty) {
                element.children.push({
                  id: 0,
                  label: "无",
                  parentId: 0,
                  children: null,
                });
              }
              element.children = element.children;
              result[key] = element;
            }
            state.songMetadataTags = result;
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    // 获取拨片列表
    async fetchPickList({ state, commit }) {
      if (state.dataLoaded.pick) return;
      try {
        const resp = await api.PICK_SEARCH({
          ...state.pageConfig,
        });
        commit("SET_PICK_LIST", resp.data.data?.list ?? []);
        console.log(resp.data.data?.list, "state");
      } catch (error) {
        console.error("获取拨片列表失败:", error);
      }
    },
    // 获取鼓机分类
    async fetchDrumCategories({ state, commit }) {
      if (state.dataLoaded.drum) return;
      try {
        const res = await api.DRUM_SEARCH({
          ...state.pageConfig,
        });
        commit("SET_DRUM_CATEGORIES", res.data.data?.list ?? []);
      } catch (error) {
        console.error("获取鼓机分类失败:", error);
      }
    },

    // 获取轨道音色选项
    async fetchTrackNoteOptions({ commit }) {
      try {
        const params = {
          firstSet: ["sound_c2", "scard_01"],
          second: "note",
        };
        const res = await api.getAssetsCategoryTreeDataList(params);
        if (res.data.code === 1) {
          const trackNoteOptions = formatTimbreOptions(res.data.data);
          // 存储到 Vuex
          commit("SET_TRACK_NOTE_OPTIONS", trackNoteOptions);
        }
      } catch (error) {
        console.error("获取轨道音色列表失败:", error);
        // 这里可以通过回调或其他方式通知组件错误信息
      }
    },
    // 根据code获取拨片名称（支持单个或多个code）
    async getPickNameByCode({ state }, code) {
      if (!code) return "";
      // 查询对应code的categoryName+name
      try {
        const resp = await api.getPickList({
          code: code,
        });
        const list = resp.data.data?.list ?? [];
        const pick = list.find((item) => item.code === code);
        return pick ? `${pick.categoryName}-${pick.name}` : code;
      } catch (error) {
        console.error("获取拨片名称失败:", error);
        return code;
      }
    },

    // 根据code获取鼓机名称（支持单个或多个code）
    async getDrumNameByCode({ state }, code) {
      if (!code) return "";
      // 查询对应code的categoryName+name
      try {
        const resp = await api.getDrumList({
          code: code,
        });
        const list = resp.data.data?.list ?? [];
        const drum = list.find((item) => item.code === code);
        return drum ? `${drum.categoryName}-${drum.name}` : code;
      } catch (error) {
        console.error("获取鼓机名称失败:", error);
        return code;
      }
    },

    // 检查鼓机事件配置
    checkDrumEvents({ commit }, privateRows) {
      try {
        // 检查私有轨中的鼓机事件和情绪鼓机事件
        let lastDrumEvent = null;
        let lastDrumEventCellIndex = -1;
        let lastU1DrumEvent = null;
        let lastU1DrumEventCellIndex = -1;

        if (privateRows) {
          for (const row of privateRows) {
            // 检查是否是鼓机类型的轨道
            if (row.type === EVENT_TYPES.DRUM) {
              // DRUM 类型
              // 遍历该行的所有单元格
              for (let i = 0; i < row.cells.length; i++) {
                const cell = row.cells[i];
                // 检查单元格是否有活动事件
                if (cell.active && cell.event) {
                  lastDrumEvent = cell.event;
                  lastDrumEventCellIndex = i;
                }
              }
            }
            // 检查是否是情绪鼓机类型的轨道
            if (row.type === EVENT_TYPES.U1_DRUM) {
              // U1_DRUM 类型
              // 遍历该行的所有单元格
              for (let i = 0; i < row.cells.length; i++) {
                const cell = row.cells[i];
                // 检查单元格是否有活动事件
                if (cell.active && cell.event) {
                  lastU1DrumEvent = cell.event;
                  lastU1DrumEventCellIndex = i;
                }
              }
            }
          }
        }

        // 检查结果
        const drumHasOpen = lastDrumEvent !== null;
        const drumHasClose = drumHasOpen && lastDrumEvent.switchType === 0;
        const u1DrumHasOpen = lastU1DrumEvent !== null;
        const u1DrumHasClose =
          u1DrumHasOpen && lastU1DrumEvent.switchType === 0;

        // 构建错误信息
        const errorMessages = [];
        if (drumHasOpen && !drumHasClose) {
          errorMessages.push("鼓机");
        }
        if (u1DrumHasOpen && !u1DrumHasClose) {
          errorMessages.push("情绪鼓机");
        }

        // 如果有错误
        if (errorMessages.length > 0) {
          return {
            success: false,
            message: `${errorMessages.join("和")}没有配置关鼓机，请检查`,
          };
        }

        // 没有鼓机事件或最后一个是关鼓事件
        return {
          success: true,
          message: "鼓机事件配置正确",
        };
      } catch (error) {
        console.error("检查鼓机事件失败:", error);
        return {
          success: false,
          message: "检查鼓机事件时发生错误",
        };
      }
    },
  },
  getters: {
    pickList: (state) => state.pickList,
    drumCategories: (state) => state.drumCategories,
    // 根据code获取拨片名称
    getPickNameByCode: (state) => (code) => {
      const pick = state.pickList.find((item) => item.code === code);
      return pick ? pick.name : "未知拨片";
    },
    // 根据code获取鼓机名称
    getDrumNameByCode: (state) => (code) => {
      const drum = state.drumCategories.find((item) => item.code === code);
      return drum ? drum.name : "未知鼓机";
    },
    // 拨片和鼓机配置
    rhythmConfig: (state) => state.rhythmConfig,
    // 外置音色卡配置
    externalTimbreCards: (state) => state.externalTimbreCards,
  },
};
